import Icon from '@forwardflow/ui/Icons'

import { Link, useParams } from 'react-router-dom'
import { useMemo } from 'react'
import { useGetUserDetailsByEmail } from '@forwardflow/hooks/customHooks/organization/useGetUserDetailsByEmail'
import { Avatar, Skeleton } from 'antd'
import { UserAccessPermission } from '../UserAccessPermission'

const InfoRow = ({ label, value }: { label: string; value?: string | null }) => {
   if (!value) return null
   return (
      <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
         <span className="text-[12px] text-white/40">{label}</span>
         <span className="text-[13px] text-white/80">{value}</span>
      </div>
   )
}

const ViewUserAccount = () => {
   const { id: email } = useParams()

   const { data: userDetails, isLoading } = useGetUserDetailsByEmail(
      email || ''
   )
   const initials =
      userDetails?.firstName?.charAt(0) +
      (userDetails?.lastName?.charAt(0) || '')

   const avatarColor = useMemo(() => {
      const name = `${userDetails?.firstName || ''}${
         userDetails?.lastName || ''
      }`
      const hash = name
         .split('')
         .reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0)

      const colors = [
         '#f56a00',
         '#7265e6',
         '#ffbf00',
         '#00a2ae',
         '#712704',
         '#04B404',
         '#0174DF',
         '#8A0886',
         '#DF0101',
         '#04B4AE',
      ]

      const colorIndex = Math.abs(hash) % colors.length
      return colors[colorIndex]
   }, [userDetails])

   if (isLoading) {
      return (
         <div className="flex h-full">
            <Skeleton active></Skeleton>
         </div>
      )
   }

   const calendarProvider = userDetails?.data?.calendarInfo?.provider
   const calendarEmail = userDetails?.data?.calendarInfo?.email
   const appointmentData = userDetails?.appointmentData

   return (
      <div className="flex flex-col flex-1 h-full overflow-hidden">
         <div className="flex items-center h-20 pl-4 pr-4 bg-white/5 shrink-0">
            <Link
               to="/settings/user-management"
               className="flex items-center"
            >
               <Icon
                  name="arrow-right"
                  className="scale-[0.6] rotate-180 cursor-pointer fill-white mr-2"
               />
               <span className="text-sm font-medium opacity-70">User Management</span>
            </Link>
            <span className="mx-2 text-white/30">/</span>
            <span className="text-sm font-medium">
               {userDetails?.firstName || ''} {userDetails?.lastName || ''}
            </span>
         </div>
         <div className="flex-1 overflow-y-auto">
            {/* Header card */}
            <div className="flex items-center py-5 pl-10 pr-10 bg-primary/10">
               <Avatar
                  className="w-12 h-12 mr-5 font-semibold shrink-0 text-base"
                  style={{ backgroundColor: avatarColor }}
               >
                  {initials}
               </Avatar>
               <div className="mr-auto min-w-0">
                  <p className="text-lg font-medium truncate leading-tight">
                     {userDetails?.firstName || ''} {userDetails?.lastName || ''}
                  </p>
                  <p className="text-[13px] text-white/50 truncate mt-0.5">
                     {userDetails?.email || ''}
                  </p>
                  {userDetails?.linkedEmail && userDetails.linkedEmail !== userDetails.email && (
                     <p className="text-[12px] text-primary/70 truncate mt-0.5 flex items-center gap-1.5">
                        <span className="text-white/30">Login:</span> {userDetails.linkedEmail}
                     </p>
                  )}
               </div>
               {userDetails?.roles?.[0] && (
                  <span className="text-[11px] text-white/70 py-1 px-3 bg-black/20 rounded shrink-0">
                     {userDetails.roles[0]}
                  </span>
               )}
            </div>

            {/* User details section */}
            <div className="px-10 pt-6 pb-3 text-[11px] tracking-widest uppercase text-white/30 font-medium">
               User Details
            </div>
            <div className="mx-10 px-5 py-1 rounded-md bg-primary/10">
               <InfoRow label="Phone" value={userDetails?.phone} />
               <InfoRow label="Email" value={userDetails?.email} />
               {userDetails?.linkedEmail && userDetails.linkedEmail !== userDetails.email && (
                  <InfoRow label="Linked Email" value={userDetails.linkedEmail} />
               )}
               <InfoRow label="Source" value={userDetails?.userSource === 'ff' ? 'Forward Flow' : userDetails?.userSource} />
               {userDetails?.invitationSentAt && (
                  <InfoRow label="Invited" value={new Date(userDetails.invitationSentAt).toLocaleDateString()} />
               )}
               <InfoRow
                  label="Whitelisted"
                  value={userDetails?.isWhiteListed ? 'Yes' : 'No'}
               />
            </div>

            {/* Calendar section */}
            {(calendarProvider || calendarEmail) && (
               <>
                  <div className="px-10 pt-6 pb-3 text-[11px] tracking-widest uppercase text-white/30 font-medium">
                     Calendar
                  </div>
                  <div className="mx-10 px-5 py-1 rounded-md bg-primary/10">
                     <InfoRow label="Provider" value={calendarProvider ? calendarProvider.charAt(0).toUpperCase() + calendarProvider.slice(1) : null} />
                     <InfoRow label="Calendar Email" value={calendarEmail} />
                  </div>
               </>
            )}

            {/* Appointment section */}
            {appointmentData && (
               <>
                  <div className="px-10 pt-6 pb-3 text-[11px] tracking-widest uppercase text-white/30 font-medium">
                     Appointment Preference
                  </div>
                  <div className="mx-10 px-5 py-1 rounded-md bg-primary/10">
                     <InfoRow label="Type" value={appointmentData.appointmentType} />
                     <InfoRow label="Location" value={appointmentData.appointmentLocationType} />
                     <InfoRow label="Platform" value={appointmentData.meetingPlatform} />
                     {appointmentData.meetingLink && (
                        <InfoRow label="Meeting Link" value={appointmentData.meetingLink} />
                     )}
                     {appointmentData.meetingAddress && (
                        <InfoRow label="Address" value={[appointmentData.meetingAddress.street, appointmentData.meetingAddress.city, appointmentData.meetingAddress.state].filter(Boolean).join(', ')} />
                     )}
                  </div>
               </>
            )}

            {/* Access permissions - inline instead of separate component */}
            <div className="px-10 pt-6 pb-3 text-[11px] tracking-widest uppercase text-white/30 font-medium">
               Access Permissions
            </div>
            <div className="mx-10 mb-8">
               <UserAccessPermission
                  email={userDetails?.email || ''}
                  userId={userDetails?.id?.toString() || ''}
               />
            </div>
         </div>
      </div>
   )
}
export default ViewUserAccount
